import { useState, useMemo, useCallback } from 'react'
import {
  Search, ChevronRight, Clock, MapPin, Users,
  FileText, Paperclip, Download, Plus, Calendar,
  SlidersHorizontal, X,
} from 'lucide-react'
import { useWeeklyMoms, useWeeklyStats, useNextSchedule } from '@/hooks/useWeeklyMom'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'

const MONTH_ID = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agt','Sep','Okt','Nov','Des']
const MONTH_FULL_ID = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember']
const DAY_ID = ['Minggu','Senin','Selasa','Rabu','Kamis','Jumat','Sabtu']

const CURRENT_YEAR = String(new Date().getFullYear())
const YEAR_TABS = ['Semua MoM', CURRENT_YEAR, String(+CURRENT_YEAR - 1), String(+CURRENT_YEAR - 2)]

const DIVISI_OPTIONS = ['Semua Divisi', 'PP', 'ME', 'OA', 'OPS', 'OD', 'Digital']

const STATUS_META = {
  selesai:     { label: 'Selesai',     cls: 'bg-green-50 text-green-700 border-green-200' },
  dijadwalkan: { label: 'Dijadwalkan', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  dibatalkan:  { label: 'Dibatalkan',  cls: 'bg-red-50 text-red-700 border-red-200' },
}

const TOPICS = [
  { label: 'Program & Proyek',       pct: 28 },
  { label: 'Keuangan & Anggaran',    pct: 22 },
  { label: 'Operasional',            pct: 18 },
  { label: 'SDM & Organisasi',       pct: 15 },
  { label: 'Komunikasi & Publikasi', pct: 10 },
  { label: 'Lainnya',               pct: 7  },
]

function formatFullDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00')
  return `${DAY_ID[d.getDay()]}, ${d.getDate()} ${MONTH_FULL_ID[d.getMonth()]} ${d.getFullYear()}`
}

// ── Add MoM Modal ─────────────────────────────────────────────────────────────
function AddMomModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const today = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({
    meeting_date: today,
    time_start: '09:00', time_end: '10:30',
    location: 'Online (Google Meet)',
    participants: '',
    led_by: 'Direktur Eksekutif',
    status: 'dijadwalkan',
    divisi: '',
    mom_url: '',
    attachments_count: '0',
    notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.meeting_date) { setErr('Tanggal rapat wajib diisi.'); return }
    setSaving(true)
    const dateLabel = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Jakarta' })
      .format(new Date(form.meeting_date + 'T00:00:00'))
    const { error } = await supabase.from('weekly_moms').insert({
      title:            `Weekly Update CERAH – ${dateLabel}`,
      meeting_date:     form.meeting_date,
      time_start:       form.time_start,
      time_end:         form.time_end,
      location:         form.location,
      participants:     parseInt(form.participants) || 0,
      led_by:           form.led_by,
      status:           form.status,
      divisi:           form.divisi || null,
      mom_url:          form.mom_url || null,
      attachments_count: parseInt(form.attachments_count) || 0,
      notes:            form.notes || null,
    })
    setSaving(false)
    if (error) { setErr('Gagal menyimpan. Coba lagi.'); return }
    onSaved()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-cerah-navy">Buat MoM Baru</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto px-6 py-5 flex flex-col gap-4">
          {/* Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Tanggal Rapat <span className="text-red-500">*</span></label>
              <input type="date" value={form.meeting_date} onChange={e => set('meeting_date', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal bg-white text-gray-700">
                <option value="dijadwalkan">Dijadwalkan</option>
                <option value="selesai">Selesai</option>
                <option value="dibatalkan">Dibatalkan</option>
              </select>
            </div>
          </div>

          {/* Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Waktu Mulai</label>
              <input type="time" value={form.time_start} onChange={e => set('time_start', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Waktu Selesai</label>
              <input type="time" value={form.time_end} onChange={e => set('time_end', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal" />
            </div>
          </div>

          {/* Location + Led by */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Lokasi</label>
            <input type="text" value={form.location} onChange={e => set('location', e.target.value)}
              placeholder="Online (Google Meet), Ruang Rapat, dll."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Dipimpin oleh</label>
              <input type="text" value={form.led_by} onChange={e => set('led_by', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Jumlah Peserta</label>
              <input type="number" min="0" value={form.participants} onChange={e => set('participants', e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal" />
            </div>
          </div>

          {/* Divisi + Attachments */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Divisi</label>
              <select value={form.divisi} onChange={e => set('divisi', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal bg-white text-gray-700">
                <option value="">Semua / Umum</option>
                {['PP','ME','OA','OPS','OD','Digital'].map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Jumlah Lampiran</label>
              <input type="number" min="0" value={form.attachments_count} onChange={e => set('attachments_count', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal" />
            </div>
          </div>

          {/* MoM URL */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Link Dokumen MoM</label>
            <input type="url" value={form.mom_url} onChange={e => set('mom_url', e.target.value)}
              placeholder="https://drive.google.com/..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal" />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Catatan / Agenda</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Topik bahasan, agenda rapat, atau catatan tambahan..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal resize-none" />
          </div>

          {err && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-md">{err}</p>}
        </form>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Batal
          </button>
          <button onClick={handleSubmit as unknown as React.MouseEventHandler} disabled={saving}
            className="px-5 py-2 text-sm font-semibold bg-cerah-teal text-white rounded-lg hover:bg-cerah-teal/90 disabled:opacity-50 transition-colors">
            {saving ? 'Menyimpan...' : 'Simpan MoM'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function WeeklyUpdate() {
  const [activeTab, setActiveTab]   = useState('Semua MoM')
  const [divisi, setDivisi]         = useState('Semua Divisi')
  const [search, setSearch]         = useState('')
  const [showAll, setShowAll]       = useState(false)
  const [showModal, setShowModal]   = useState(false)

  const yearFilter = activeTab === 'Semua MoM' ? 'semua' : activeTab
  const divisiFilter = divisi === 'Semua Divisi' ? 'semua' : divisi

  const { moms, loading, refetch } = useWeeklyMoms({ year: yearFilter, divisi: divisiFilter, search })
  const { stats }                  = useWeeklyStats(CURRENT_YEAR)
  const { next: nextSched }        = useNextSchedule()

  const displayed = useMemo(() => (showAll ? moms : moms.slice(0, 5)), [moms, showAll])
  const handleSaved = useCallback(() => refetch(), [refetch])

  return (
    <div className="flex h-full">

      {/* ── Main ── */}
      <div className="flex-1 min-w-0 overflow-y-auto p-6">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
          <span>What's Up CERAH</span>
          <ChevronRight size={12} />
          <span className="text-gray-600 font-medium">Weekly Update (Weekly Meeting - MoM)</span>
        </nav>

        {/* Header row */}
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h1 className="text-xl font-bold text-cerah-navy">Weekly Update (Weekly Meeting - MoM)</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Informasi dan notulen rapat mingguan CERAH untuk memastikan semua tim selaras dan terinformasi.
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="shrink-0 flex items-center gap-1.5 px-4 py-2 bg-cerah-teal text-white text-sm font-semibold rounded-lg hover:bg-cerah-teal/90 transition-colors"
          >
            <Plus size={15} />
            Buat MoM Baru
          </button>
        </div>

        {/* Year tabs */}
        <div className="flex gap-1 border-b border-gray-200 mt-5 mb-4">
          {YEAR_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setShowAll(false) }}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
                activeTab === tab
                  ? 'border-cerah-teal text-cerah-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex gap-2 mb-5">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Cari MoM berdasarkan judul, topik, atau pembahasan..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal"
            />
          </div>
          <select
            value={divisi}
            onChange={e => setDivisi(e.target.value)}
            className="px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal bg-white text-gray-600"
          >
            {DIVISI_OPTIONS.map(d => <option key={d}>{d}</option>)}
          </select>
          <button className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors">
            <SlidersHorizontal size={13} />
          </button>
        </div>

        {/* List */}
        <h2 className="text-sm font-semibold text-cerah-navy mb-3">MoM Terbaru</h2>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-cerah-teal border-t-transparent rounded-full animate-spin" />
          </div>
        ) : moms.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 text-sm gap-2">
            <FileText size={32} className="opacity-30" />
            <p>Belum ada MoM ditemukan.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {displayed.map(mom => {
                const d = new Date(mom.meeting_date + 'T00:00:00')
                const monthAbbr = MONTH_ID[d.getMonth()].toUpperCase()
                const day = d.getDate()
                const meta = STATUS_META[mom.status]
                return (
                  <div key={mom.id} className="flex items-center gap-4 px-4 py-3.5 bg-white hover:bg-gray-50 transition-colors">

                    {/* Date badge */}
                    <div className="shrink-0 w-12 flex flex-col items-center">
                      <span className="text-[10px] font-bold text-cerah-teal uppercase">{monthAbbr}</span>
                      <span className="text-2xl font-bold text-cerah-navy leading-none">{day}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-cerah-navy truncate">{mom.title}</span>
                        <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', meta.cls)}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock size={11} />
                          {mom.meeting_date.replace(/-/g, '/')} · {mom.time_start} – {mom.time_end} WIB
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={11} />
                          {mom.location}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-0.5">
                        <Users size={11} />
                        <span>Peserta: {mom.participants} orang</span>
                        <span>·</span>
                        <span>Dipimpin oleh: {mom.led_by}</span>
                      </div>
                    </div>

                    {/* Links */}
                    <div className="shrink-0 flex items-center gap-3">
                      {mom.mom_url ? (
                        <a
                          href={mom.mom_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-cerah-teal hover:underline"
                        >
                          <FileText size={13} />
                          MoM
                        </a>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-gray-300">
                          <FileText size={13} />
                          MoM
                        </span>
                      )}
                      {mom.attachments_count > 0 && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Paperclip size={13} />
                          Lampiran ({mom.attachments_count})
                        </span>
                      )}
                      <ChevronRight size={16} className="text-gray-300" />
                    </div>
                  </div>
                )
              })}
            </div>

            {moms.length > 5 && (
              <button
                onClick={() => setShowAll(v => !v)}
                className="w-full mt-3 py-2.5 text-sm text-cerah-teal font-medium hover:underline"
              >
                {showAll ? 'Sembunyikan' : `Lihat semua MoM (${moms.length})`}
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Right sidebar ── */}
      <aside className="w-72 shrink-0 border-l border-gray-100 overflow-y-auto p-5 flex flex-col gap-5 bg-white">

        {/* Next schedule */}
        <div className="rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-cerah-navy mb-3">Jadwal Weekly Update Berikutnya</h3>
          {nextSched ? (
            <div className="flex gap-3 items-start">
              <div className="w-10 h-10 rounded-lg bg-cerah-teal-light flex items-center justify-center shrink-0">
                <Calendar size={20} className="text-cerah-teal" />
              </div>
              <div>
                <p className="text-sm font-bold text-cerah-navy">{formatFullDate(nextSched.meeting_date)}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                  <Clock size={11} /> {nextSched.time_start} – {nextSched.time_end} WIB
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                  <MapPin size={11} /> {nextSched.location}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400">Belum ada jadwal berikutnya.</p>
          )}
          <button className="mt-3 w-full py-1.5 bg-cerah-teal text-white text-xs font-semibold rounded-lg hover:bg-cerah-teal/90 transition-colors">
            Lihat Agenda
          </button>
        </div>

        {/* Stats */}
        <div className="rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-cerah-navy mb-3">Statistik MoM {CURRENT_YEAR}</h3>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <p className="text-lg font-bold text-cerah-navy">{stats.total}</p>
              <p className="text-[10px] text-gray-400 leading-tight">Total MoM</p>
            </div>
            <div>
              <p className="text-lg font-bold text-green-600">{stats.selesai}</p>
              <p className="text-[10px] text-gray-400 leading-tight">Selesai</p>
            </div>
            <div>
              <p className="text-lg font-bold text-blue-600">{stats.dijadwalkan}</p>
              <p className="text-[10px] text-gray-400 leading-tight">Dijadwalkan</p>
            </div>
            <div>
              <p className="text-lg font-bold text-red-500">{stats.dibatalkan}</p>
              <p className="text-[10px] text-gray-400 leading-tight">Dibatalkan</p>
            </div>
          </div>
        </div>

        {/* Topics */}
        <div className="rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-cerah-navy mb-3">Topik Pembahasan Teratas</h3>
          <div className="flex flex-col gap-2.5">
            {TOPICS.map(t => (
              <div key={t.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">{t.label}</span>
                  <span className="text-gray-400 font-medium">{t.pct}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-cerah-teal rounded-full"
                    style={{ width: `${t.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Panduan */}
        <div className="rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-cerah-navy mb-3">Panduan & Template</h3>
          <div className="flex flex-col gap-2">
            {[
              { title: 'Template MoM Weekly Update', desc: 'Unduh template standar MoM weekly meeting' },
              { title: 'Panduan Penyusunan MoM',     desc: 'Panduan lengkap penyusunan MoM CERAH' },
            ].map(item => (
              <div
                key={item.title}
                className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-100 hover:border-cerah-teal/30 hover:bg-cerah-teal-light/30 transition-colors cursor-pointer group"
              >
                <FileText size={16} className="text-cerah-teal shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-cerah-navy truncate">{item.title}</p>
                  <p className="text-[10px] text-gray-400 truncate">{item.desc}</p>
                </div>
                <Download size={13} className="text-gray-300 group-hover:text-cerah-teal transition-colors shrink-0" />
              </div>
            ))}
          </div>
        </div>

      </aside>

      {showModal && (
        <AddMomModal
          onClose={() => setShowModal(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}
